import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Icon, Intent } from '@blueprintjs/core';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import Toast from 'helpers/toaster';
import { PHOTO_ACCEPT, PHOTO_MAX_MB, photoProblem, photoUrls, uploadErrorText } from 'helpers/equipmentPhotos';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// An equipment's photos (its page): thumbnails that open full size, "Add
// photos" (several at once; each checked first), and delete behind a prompt.
@inject('equipmentStore') @observer
class EquipmentPhotos extends Component {
  @observable openIndex = -1;
  @observable deleting = null; // the photo waiting on the delete prompt
  @observable problem = null;
  @observable uploading = false;

  get photos() {
    return (this.props.equipment.attributes.images || []).map(photoUrls).filter(Boolean);
  }

  refresh() {
    return this.props.equipmentStore.loadEquipmentForImage({ uuid: this.props.equipment.id });
  }

  onPick = (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    input.value = ''; // the same file can be picked again after fixing it
    if (files.length === 0) return;
    const problems = files.map(photoProblem);
    const ok = files.filter((file, index) => !problems[index]);
    const firstProblem = problems.find(Boolean);
    this.problem = firstProblem
      ? (files.length > 1 ? `${files[problems.indexOf(firstProblem)].name}: ${firstProblem}` : firstProblem)
      : null;
    if (ok.length === 0) return;
    this.uploading = true;
    this.props.equipmentStore.attach_image(ok, this.props.equipment.id)
      .then(() => this.refresh())
      .then(() => {
        Toast.show({ message: ok.length === 1 ? 'Photo added.' : `${ok.length} photos added.`, intent: Intent.SUCCESS });
      })
      .catch((error) => { this.problem = uploadErrorText(error); })
      .finally(() => { this.uploading = false; });
  };

  confirmDelete = () => {
    const photo = this.deleting;
    this.deleting = null;
    this.props.equipmentStore.removeAttachment({ id: photo.id }, this.props.equipment.id)
      .then(() => this.refresh())
      .then(() => { Toast.show({ message: 'Photo deleted.', intent: Intent.SUCCESS }); })
      .catch(() => { Toast.show({ message: "Couldn't delete the photo. Try again.", intent: Intent.DANGER }); });
  };

  render() {
    const photos = this.photos;
    return (
      <section className="up-card eqp-photos" aria-labelledby="eqp-photos-title">
        <header className="up-card__head">
          <div className="eqp-card-title">
            <h2 className="up-card__title" id="eqp-photos-title">Photos</h2>
            {photos.length > 0 && <span className="up-card__note">{plural(photos.length, 'photo')}</span>}
          </div>
          {/* The input is hidden; its label is the button (focus shows on it). */}
          <input
            id="eqp-photo-input"
            className="sr-only"
            type="file"
            multiple
            accept={PHOTO_ACCEPT}
            disabled={this.uploading}
            aria-describedby="eqp-photo-help"
            onChange={this.onPick}
          />
          <label className={`bp3-button up-button eqp-upload${this.uploading ? ' is-busy' : ''}`} htmlFor="eqp-photo-input">
            <Icon icon={this.uploading ? 'time' : 'camera'} iconSize={14} />
            <span>{this.uploading ? 'Uploading…' : 'Add photos'}</span>
          </label>
        </header>

        {photos.length > 0 ? (
          <ul className="eqp-thumbs">
            {photos.map((photo, index) => (
              <li key={photo.id || index} className="eqp-thumb">
                <button type="button" className="eqp-thumb__open" aria-label={`Open photo ${index + 1}`} onClick={() => { this.openIndex = index; }}>
                  <img src={photo.thumbnail} alt="" />
                </button>
                <button type="button" className="eqp-thumb__delete" aria-label={`Delete photo ${index + 1}`} title="Delete photo" onClick={() => { this.deleting = photo; }}>
                  <Icon icon="trash" iconSize={12} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="up-empty">No photos yet. A photo of the data plate saves a trip later.</p>
        )}

        {this.problem
          ? <p className="eqp-photo-problem" id="eqp-photo-help" role="alert">{this.problem}</p>
          : <p className="up-card__note" id="eqp-photo-help">JPG, PNG or GIF, up to {PHOTO_MAX_MB} MB each.</p>}

        <Lightbox
          open={this.openIndex >= 0}
          close={() => { this.openIndex = -1; }}
          slides={photos.map(({ src, thumbnail }) => ({ src, thumbnail }))}
          plugins={[Zoom, Thumbnails]}
          index={Math.max(this.openIndex, 0)}
          zoom={{ maxZoomPixelRatio: 10, zoomInMultiplier: 1.5 }}
        />

        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete photo"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>Delete this photo?</strong>
          <p>It’s removed from this equipment right away and can’t be brought back.</p>
        </Alert>
      </section>
    );
  }
}

export default EquipmentPhotos;
